/* ============================================================
   PintaFlow — Design Direction: "ATELIÉR" (Premium / Final)
   ----------------------------------------------------------
   Aesop × Hermès × Linear's calmer moments × Apple white papers

   Principles:
   - Light, warm bone background (dark mode masked the contrast issue)
   - Pure monochrome — no color accent except one tiny aged-brass seal
   - Massive Fraunces serif (96-128px) at light optical weight
   - One idea per scroll, generous vertical rhythm
   - Pipeline shown as a numbered editorial list, not a dashboard
   - Restraint > maximalism. Copy is confident, never busy.
   ============================================================ */
import { ArrowUpRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const BG = "#F1ECE2";
const INK = "#161514";
const INK_60 = "rgba(22,21,20,0.60)";
const INK_40 = "rgba(22,21,20,0.40)";
const INK_12 = "rgba(22,21,20,0.12)";
const SEAL = "#8C6A39";

const STAGES = [
  ["Idea", "Capture every spark, fast."],
  ["Research", "Notes, links, quiet thinking."],
  ["Script", "Where it becomes shape."],
  ["Create", "Record. Edit. Refine."],
  ["Schedule", "Choose the moment."],
  ["Published", "Out in the world."],
];

const PRINCIPLES = [
  "Idea Vault — every spark, kept.",
  "Swipe File — references, indexed.",
  "Pipeline — six rooms, one craft.",
  "Calendar — a publishing month, at rest.",
  "Streak — quiet consistency, made visible.",
  "Goals — your rhythm, your terms.",
];

export default function LandingPremium() {
  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{ background: BG, color: INK, fontFamily: "'Inter', -apple-system, system-ui, sans-serif" }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght,SOFT@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500&display=swap"
        rel="stylesheet"
      />
      <style>{`
        .display { font-family: 'Fraunces', serif; font-variation-settings: 'opsz' 144, 'SOFT' 50, 'wght' 400; letter-spacing: -0.03em; }
        .display-italic { font-family: 'Fraunces', serif; font-variation-settings: 'opsz' 144, 'SOFT' 80, 'wght' 400; font-style: italic; letter-spacing: -0.025em; }
        .eyebrow { font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.32em; text-transform: uppercase; }
        .seal-dot { display:inline-block; width:6px; height:6px; border-radius:50%; background:${SEAL}; vertical-align: middle; }
        .hairline { border-color: ${INK_12}; }
      `}</style>

      {/* ───────────────────── NAV ───────────────────── */}
      <header className="border-b" style={{ borderColor: INK_12 }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="seal-dot" />
            <span
              className="text-[17px] tracking-tight"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 24, 'wght' 500" }}
            >
              PintaFlow
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-12 text-sm" style={{ color: INK_60 }}>
            <span>The Studio</span>
            <span>Principles</span>
            <span>The Practice</span>
          </nav>
          <button
            onClick={startGoogleLogin}
            className="text-sm hover:opacity-60 transition"
            style={{ color: INK }}
          >
            Sign in
          </button>
        </div>
      </header>

      {/* ───────────────────── HERO ─────────────────────
          One quiet idea. Generous height. No dashboard. */}
      <section className="relative">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-24 md:pt-40 pb-28 md:pb-44">
          <div className="eyebrow mb-10" style={{ color: INK_40 }}>
            <span className="seal-dot mr-3" /> Volume 01 — Now in private beta
          </div>

          <h1
            className="display text-[56px] sm:text-[88px] md:text-[120px] lg:text-[140px] leading-[0.92]"
            style={{ color: INK }}
          >
            The studio<br />
            behind every<br />
            <span className="display-italic">published</span> thing.
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-16 md:mt-20">
            <div className="md:col-span-7" />
            <div className="md:col-span-5">
              <p className="text-[18px] md:text-[20px] leading-[1.65]" style={{ color: INK_60 }}>
                PintaFlow is the operating system serious creators use to move an idea from spark to publish.
                Calm, opinionated, and quietly powerful — the workshop, not another tool.
              </p>
              <div className="mt-10 flex flex-col gap-3">
                <button
                  onClick={startGoogleLogin}
                  className="group inline-flex items-center justify-between border rounded-full pl-6 pr-4 py-3.5 transition hover:opacity-80 max-w-[280px]"
                  style={{ borderColor: INK, background: INK, color: BG }}
                >
                  <span className="text-[14px] font-medium tracking-tight">Open the studio</span>
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center transition group-hover:rotate-45"
                    style={{ background: BG, color: INK }}
                  >
                    <ArrowUpRight size={13} strokeWidth={1.75} />
                  </span>
                </button>
                <span className="eyebrow" style={{ color: INK_40 }}>
                  Sign in with Google · Free in beta
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────── PAUSE LINE ───────────────────── */}
      <div className="border-t" style={{ borderColor: INK_12 }} />

      {/* ───────────────────── MANIFESTO ─────────────────────
          A single thought, displayed like a museum wall card. */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-44">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className="eyebrow" style={{ color: INK_40 }}>i. The premise</div>
          </div>
          <div className="md:col-span-9">
            <p
              className="display text-[32px] sm:text-[44px] md:text-[56px] leading-[1.15]"
              style={{ color: INK, fontVariationSettings: "'opsz' 96, 'wght' 400" }}
            >
              We don&apos;t build tools for creators.{" "}
              <span style={{ color: INK_40 }}>
                We build the <span className="display-italic" style={{ color: INK }}>workshop</span>{" "}
                where serious work is kept, refined, and shipped — without the noise of a dozen tools.
              </span>
            </p>
          </div>
        </div>
      </section>

      <div className="border-t" style={{ borderColor: INK_12 }} />

      {/* ───────────────────── THE PIPELINE — AS A LIST ─────────────────────
          Not a dashboard. An editorial table of contents for the workflow. */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-44">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="md:col-span-3">
            <div className="eyebrow" style={{ color: INK_40 }}>ii. The pipeline</div>
          </div>
          <div className="md:col-span-9">
            <h2
              className="display text-[40px] sm:text-[64px] md:text-[88px] leading-[0.98]"
              style={{ color: INK }}
            >
              Six rooms.<br />
              <span className="display-italic">One</span> craft.
            </h2>
            <p className="mt-8 text-[17px] md:text-[19px] leading-[1.6] max-w-xl" style={{ color: INK_60 }}>
              Every idea moves through the same quiet arc. PintaFlow keeps the rooms — you keep the work.
            </p>
          </div>
        </div>

        {/* Six rooms as a numbered editorial list with a thin spine */}
        <div className="md:pl-[25%]">
          <ol className="relative">
            {STAGES.map(([name, copy], i) => (
              <li
                key={name}
                className="grid grid-cols-12 gap-6 py-7 md:py-9 border-t group cursor-default"
                style={{ borderColor: INK_12 }}
              >
                <div
                  className="col-span-2 md:col-span-1 eyebrow pt-2"
                  style={{ color: INK_40 }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-10 md:col-span-7">
                  <div
                    className="text-[28px] sm:text-[36px] md:text-[44px] leading-[1.1] tracking-tight"
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontVariationSettings: "'opsz' 72, 'wght' 400",
                      color: INK,
                    }}
                  >
                    {name}
                  </div>
                </div>
                <div className="hidden md:flex col-span-4 items-start pt-3">
                  <p className="text-[15px] leading-[1.6]" style={{ color: INK_60 }}>
                    {copy}
                  </p>
                </div>
              </li>
            ))}
            <li className="border-t" style={{ borderColor: INK_12 }} />
          </ol>
        </div>
      </section>

      <div className="border-t" style={{ borderColor: INK_12 }} />

      {/* ───────────────────── RESTRAINT — what PintaFlow is NOT ───────────────────── */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-44">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className="eyebrow" style={{ color: INK_40 }}>iii. The restraint</div>
          </div>
          <div className="md:col-span-9">
            <h3
              className="display text-[32px] sm:text-[48px] md:text-[64px] leading-[1.06]"
              style={{ color: INK }}
            >
              The most luxurious thing we offer is{" "}
              <span className="display-italic">what we left out.</span>
            </h3>
            <ul className="mt-14 space-y-7 text-[19px] md:text-[22px] leading-[1.45] max-w-2xl"
                style={{ color: INK_60, fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 48, 'wght' 400" }}>
              <li className="flex gap-5">
                <span className="eyebrow pt-2 flex-shrink-0" style={{ color: INK_40 }}>—</span>
                <span>No AI writing for you. Your voice is the point.</span>
              </li>
              <li className="flex gap-5">
                <span className="eyebrow pt-2 flex-shrink-0" style={{ color: INK_40 }}>—</span>
                <span>No notifications. Your attention belongs to the work.</span>
              </li>
              <li className="flex gap-5">
                <span className="eyebrow pt-2 flex-shrink-0" style={{ color: INK_40 }}>—</span>
                <span>No vanity metrics. We don&apos;t reward noise.</span>
              </li>
              <li className="flex gap-5">
                <span className="eyebrow pt-2 flex-shrink-0" style={{ color: INK_40 }}>—</span>
                <span>No collaboration features. Solo creators, alone with the craft.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <div className="border-t" style={{ borderColor: INK_12 }} />

      {/* ───────────────────── PRACTICE — what IS included, as one-liners ───────────────────── */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-44">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-3">
            <div className="eyebrow" style={{ color: INK_40 }}>iv. The practice</div>
          </div>
          <div className="md:col-span-9">
            <h3
              className="display text-[32px] sm:text-[48px] md:text-[64px] leading-[1.06]"
              style={{ color: INK }}
            >
              What stays. <span className="display-italic" style={{ color: INK_40 }}>The instruments.</span>
            </h3>
          </div>
        </div>

        <div className="md:pl-[25%] space-y-0">
          {PRINCIPLES.map((p, i) => {
            const [head, tail] = p.split(" — ");
            return (
              <div
                key={p}
                className="grid grid-cols-12 gap-6 py-6 border-t"
                style={{ borderColor: INK_12 }}
              >
                <div className="col-span-2 md:col-span-1 eyebrow pt-2" style={{ color: INK_40 }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div
                  className="col-span-10 md:col-span-11 text-[20px] md:text-[26px] leading-[1.35]"
                  style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 48, 'wght' 400", color: INK }}
                >
                  <span>{head}</span>
                  <span style={{ color: INK_40 }} className="ml-3">— {tail}</span>
                </div>
              </div>
            );
          })}
          <div className="border-t" style={{ borderColor: INK_12 }} />
        </div>
      </section>

      <div className="border-t" style={{ borderColor: INK_12 }} />

      {/* ───────────────────── CLOSING ───────────────────── */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-44 text-center">
        <div className="eyebrow mb-10" style={{ color: INK_40 }}>
          <span className="seal-dot mr-3" /> The invitation
        </div>
        <h2
          className="display text-[48px] sm:text-[80px] md:text-[112px] leading-[0.96] max-w-4xl mx-auto"
          style={{ color: INK }}
        >
          Make space<br />for the <span className="display-italic">work.</span>
        </h2>
        <div className="mt-16 inline-flex flex-col items-center gap-4">
          <button
            onClick={startGoogleLogin}
            className="group inline-flex items-center justify-between border rounded-full pl-6 pr-4 py-3.5 transition hover:opacity-85"
            style={{ borderColor: INK, background: INK, color: BG }}
          >
            <span className="text-[14px] font-medium tracking-tight">Open the studio</span>
            <span
              className="w-7 h-7 rounded-full ml-6 flex items-center justify-center transition group-hover:rotate-45"
              style={{ background: BG, color: INK }}
            >
              <ArrowUpRight size={13} strokeWidth={1.75} />
            </span>
          </button>
          <span className="eyebrow" style={{ color: INK_40 }}>Sign in with Google · Free in private beta</span>
        </div>
      </section>

      {/* ───────────────────── FOOTER ───────────────────── */}
      <footer className="border-t" style={{ borderColor: INK_12 }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="seal-dot" />
            <span
              className="text-[15px]"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 24, 'wght' 500" }}
            >
              PintaFlow
            </span>
            <span className="eyebrow ml-4" style={{ color: INK_40 }}>est. 2026</span>
          </div>
          <div className="eyebrow" style={{ color: INK_40 }}>direction · ateliér · final</div>
        </div>
      </footer>
    </div>
  );
}
